// @ts-nocheck — ported from operators_dashboards EnrollmentDashboard.jsx UI surface
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Bar, Line } from "react-chartjs-2";

import { Skeleton } from "~/components/ui/skeleton";

import {
  ALIGNMENT_LAG_BUCKETS,
  BOOKING_SOURCE_SERIES,
  COLORS,
  SNAPSHOT_MARKETABLE_METRICS,
  asPct,
  changeColor,
  fmtN,
  formatAbsoluteChange,
  formatDuration,
  formatEtDateTime,
  formatEtTimestamp,
  formatPctChange,
  groupEsConversionWeeks,
  isWeekendOrHoliday,
  monthLabel,
  safeRate,
  shortDateLabel,
  shortWeekdayDateLabel,
  snapshotHistoryDateLabel,
  summarizeCallMetrics,
  summarizeEsConversion,
  weekRangeLabel,
  esConversionQuartiles,
} from "./enrollmentShared";
import type {
  EnrollmentCallMetrics,
  EnrollmentConversionDetail,
  EnrollmentCumulativeRow,
  EnrollmentEodCalls,
  EnrollmentEsConversionRow,
  EnrollmentEsOpsCallFailureRow,
  EnrollmentEsOpsFailureRow,
  EnrollmentSnapshotMetrics,
  EnrollmentTrendRow,
  EnrollmentClinicOption,
  EnrollmentBookingSourceRow,
  EnrollmentNeuroAppointmentRow,
  EnrollmentReferralRow,
} from "./enrollmentTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart, _args, pluginOptions) {
    if (chart.config.type !== "bar" || !pluginOptions) return;

    const { ctx, data, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;
    if (!xScale || !yScale) return;

    const totals = new Map();
    data.datasets.forEach((dataset) => {
      dataset.data.forEach((value, index) => {
        totals.set(index, (totals.get(index) || 0) + Number(value || 0));
      });
    });

    ctx.save();
    ctx.textAlign = "center";
    ctx.font = "600 11px system-ui, sans-serif";

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((element, dataIndex) => {
        const value = Number(dataset.data[dataIndex] || 0);
        if (!pluginOptions.showSegmentValues || value <= 0) return;

        const bounds = element.getProps(["x", "y", "base"], true);
        if (Math.abs(bounds.base - bounds.y) < 18) return;

        ctx.fillStyle = pluginOptions.segmentTextColor || "#ffffff";
        ctx.fillText(String(value), bounds.x, bounds.y + (bounds.base - bounds.y) / 2 + 4);
      });
    });

    if (pluginOptions.showTotals) {
      totals.forEach((total, index) => {
        if (!total) return;
        const x = xScale.getPixelForValue(index);
        const y = yScale.getPixelForValue(total);
        ctx.fillStyle = pluginOptions.totalTextColor || "#4a4540";
        ctx.fillText(String(total), x, y - 8);
      });
    }

    ctx.restore();
  },
};

const backgroundBandsPlugin = {
  id: "backgroundBands",
  beforeDatasetsDraw(chart, _args, pluginOptions) {
    const indices = pluginOptions?.indices || [];
    if (!indices.length) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale || !chartArea) return;

    ctx.save();
    ctx.fillStyle = pluginOptions.fillStyle || "rgba(148, 163, 184, 0.12)";

    indices.forEach((index) => {
      const center = xScale.getPixelForValue(index);
      const previous = index > 0 ? xScale.getPixelForValue(index - 1) : chartArea.left;
      const next =
        index < xScale.ticks.length - 1 ? xScale.getPixelForValue(index + 1) : chartArea.right;
      const left = index > 0 ? center - (center - previous) / 2 : chartArea.left;
      const right =
        index < xScale.ticks.length - 1 ? center + (next - center) / 2 : chartArea.right;
      ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    });

    ctx.restore();
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  valueLabelsPlugin,
  backgroundBandsPlugin,
);

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e0d8",
  borderRadius: 10,
  padding: "1rem 1.2rem",
};
const labelStyle = {
  fontSize: "0.78rem",
  color: "#7a7467",
  marginBottom: "0.2rem",
};
const valueStyle = { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.1 };
const subStyle = {
  fontSize: "0.78rem",
  color: "#7a7467",
  marginTop: "0.15rem",
};

export function SectionDivider({ label }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        margin: "1.75rem 0 0.9rem",
      }}
    >
      <div style={{ fontWeight: 700, fontSize: "1rem", whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ flex: 1, height: 1, background: "#e5e0d8" }} />
    </div>
  );
}

export function LoadingState() {
  return (
    <div
      className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">Loading…</span>
      <Skeleton className="h-3.5 w-[35%]" />
      <Skeleton className="mt-3 h-[180px] w-full rounded-[10px]" />
    </div>
  );
}

export function ErrorState({ message }) {
  return (
    <div role="alert" style={{ color: COLORS.red, padding: "2rem 0", fontSize: "0.88rem" }}>
      Failed to load data: {message}
    </div>
  );
}

export function HorizonTabs({ options, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          style={{
            border: value === option.value ? "2px solid #1d4ed8" : "1px solid #bfdbfe",
            background: value === option.value ? "#eff6ff" : "#fff",
            color: value === option.value ? "#1d4ed8" : "#4a4540",
            fontWeight: value === option.value ? 700 : 400,
            fontSize: "0.78rem",
            padding: "0.3rem 0.6rem",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function ComparisonMetricCard({ label, value, previousValue, accent, emphasized }) {
  return (
    <div
      style={{
        ...cardStyle,
        minWidth: emphasized ? 200 : 148,
        flex: emphasized ? "0 0 220px" : "0 0 156px",
        padding: emphasized ? "0.9rem 1rem" : "0.65rem 0.8rem",
        borderLeft: `4px solid ${accent || "#e5e0d8"}`,
      }}
    >
      <div style={{ ...labelStyle, fontWeight: emphasized ? 700 : 500 }}>{label}</div>
      <div
        style={{
          ...valueStyle,
          color: accent || "#242124",
          fontSize: emphasized ? "2rem" : "1.35rem",
        }}
      >
        {fmtN(value)}
      </div>
      <div style={{ ...subStyle, color: changeColor(value, previousValue) }}>
        {formatPctChange(value, previousValue)} ({formatAbsoluteChange(value, previousValue)}) vs
        prior day
      </div>
    </div>
  );
}

export function FormulaOperator({ symbol }) {
  return (
    <div
      aria-hidden
      style={{
        flexShrink: 0,
        fontSize: "1.35rem",
        fontWeight: 700,
        color: COLORS.gray,
        padding: "0 0.1rem",
      }}
    >
      {symbol}
    </div>
  );
}

export function FormulaRow({ items, caption }) {
  let metricIndex = 0;
  return (
    <div>
      {caption ? (
        <div
          style={{
            ...labelStyle,
            fontWeight: 600,
            color: "#242124",
            marginBottom: "0.45rem",
          }}
        >
          {caption}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          gap: "0.45rem",
          flexWrap: "nowrap",
          alignItems: "stretch",
          overflowX: "auto",
          paddingBottom: 4,
        }}
      >
        {items.map((item, index) =>
          item.type === "operator" ? (
            <FormulaOperator
              // biome-ignore lint/suspicious/noArrayIndexKey: formula tokens repeat; position disambiguates
              key={`${item.symbol}-${index}`}
              symbol={item.symbol}
            />
          ) : (
            <ComparisonMetricCard
              key={item.label}
              label={item.label}
              value={item.value}
              previousValue={item.previousValue}
              accent={item.accent}
              emphasized={metricIndex++ === 0}
            />
          ),
        )}
      </div>
    </div>
  );
}

export function SnapshotFormulaRow({ items, comparisonSnap, comparisonLabel }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {items.map((item, index) =>
        item.type === "operator" ? (
          <FormulaOperator
            // biome-ignore lint/suspicious/noArrayIndexKey: formula tokens repeat; position disambiguates
            key={`${item.symbol}-${index}`}
            symbol={item.symbol}
          />
        ) : (
          <SnapshotMetricCard
            key={item.label}
            label={item.label}
            value={item.value}
            previousValue={comparisonSnap?.[item.key]}
            comparisonLabel={comparisonLabel}
            accent={item.accent}
          />
        ),
      )}
    </div>
  );
}

export function SnapshotMetricCard({ label, value, previousValue, comparisonLabel, accent }) {
  const hasComparison = value != null && previousValue != null;

  return (
    <div role="group" aria-label={label} style={{ ...cardStyle, minWidth: 180, flex: "1 1 180px" }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ ...valueStyle, color: accent || "#242124", fontSize: "2rem" }}>
        {fmtN(value)}
      </div>
      <div
        style={{
          ...subStyle,
          color: hasComparison ? changeColor(value, previousValue) : COLORS.gray,
        }}
      >
        {hasComparison ? (
          <>
            {formatPctChange(value, previousValue)} ({formatAbsoluteChange(value, previousValue)})
            {" vs "}
            {comparisonLabel}
          </>
        ) : (
          "Comparison unavailable"
        )}
      </div>
    </div>
  );
}

export function EodSnapshotPanel({ snap, comparisonSnap, date, comparisonLabel }) {
  if (!snap) return null;

  const renderCardRow = (metrics) => (
    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
      {metrics.map(([key, label, accent]) => (
        <SnapshotMetricCard
          key={key}
          label={label}
          value={snap[key]}
          previousValue={comparisonSnap?.[key]}
          comparisonLabel={comparisonLabel}
          accent={accent}
        />
      ))}
    </div>
  );

  return (
    <div>
      <div style={subStyle}>As of {shortDateLabel(date)}</div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.9rem",
          marginTop: "0.5rem",
        }}
      >
        <SnapshotFormulaRow
          comparisonSnap={comparisonSnap}
          comparisonLabel={comparisonLabel}
          items={[
            {
              key: "aligned",
              label: "Aligned",
              value: snap.aligned,
              accent: COLORS.blue,
            },
          ]}
        />
        {renderCardRow(SNAPSHOT_MARKETABLE_METRICS)}
      </div>
    </div>
  );
}

export const SNAPSHOT_HISTORY_ROWS = [
  { key: "aligned", label: "Aligned" },
  { spacer: "enrollment-in-progress-spacer" },
  {
    key: "inProgress",
    label: "Enrollment in progress",
    emphasized: true,
  },
  { key: "pendingReferral", label: "Pending referral" },
  { key: "pendingSecondCall", label: "Pending 2nd call with family" },
  { key: "assessed", label: "Assessed" },
  { key: "submitted", label: "Submitted" },
  { spacer: "cms-review-spacer" },
  { section: "Under CMS review" },
  { key: "pending", label: "Pending" },
  { spacer: "new-total-spacer" },
  {
    key: "newCount",
    label: "New Total (excl. archived)",
    emphasized: true,
  },
  { key: "marketablePatients", label: "Marketable" },
  { spacer: "archived-spacer" },
  { section: "Archived" },
  { key: "archiveBeforeAlignedCount", label: "Archive before aligned" },
  { key: "mtdUnalignedCount", label: "Unaligned" },
  { key: "mtdUnengagedCount", label: "Unengaged" },
];

export function SnapshotHistoryTable({ rows }) {
  return (
    <div style={{ ...cardStyle, marginTop: "0.9rem", padding: 0 }}>
      <div style={{ padding: "0.9rem 1rem 0.35rem" }}>
        <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Last 7 days + month start</div>
        <div style={subStyle}>
          End-of-day carespace status history · Archive rows are running month-to-date totals
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table
          aria-label="EOD snapshot last 7 days and month start"
          style={{
            borderCollapse: "collapse",
            minWidth: 920,
            width: "100%",
            fontSize: "0.82rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f5f3ef" }}>
              <th
                scope="col"
                style={{
                  padding: "0.55rem 0.75rem",
                  textAlign: "left",
                  minWidth: 300,
                  borderBottom: "1px solid #d8d3ca",
                }}
              >
                EOD CS snapshot
              </th>
              {rows.map((row) => (
                <th
                  key={row.date}
                  scope="col"
                  style={{
                    padding: "0.55rem 0.65rem",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid #d8d3ca",
                  }}
                >
                  {snapshotHistoryDateLabel(row.date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SNAPSHOT_HISTORY_ROWS.map((metric) =>
              metric.spacer ? (
                <tr key={metric.spacer}>
                  <td colSpan={rows.length + 1} style={{ height: "0.45rem" }} />
                </tr>
              ) : metric.section ? (
                <tr key={metric.section}>
                  <th
                    scope="rowgroup"
                    colSpan={rows.length + 1}
                    style={{
                      padding: "0.34rem 0.75rem",
                      textAlign: "left",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {metric.section}
                  </th>
                </tr>
              ) : (
                <tr key={metric.id || metric.key}>
                  <th
                    scope="row"
                    style={{
                      padding: "0.34rem 0.75rem",
                      textAlign: "left",
                      fontWeight: metric.emphasized ? 700 : 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {metric.label}
                  </th>
                  {rows.map((row) => (
                    <td
                      key={row.date}
                      style={{
                        padding: "0.34rem 0.65rem",
                        textAlign: "right",
                        fontWeight: metric.emphasized ? 700 : 400,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtN(row.data?.[metric.key])}
                    </td>
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: { ticks: { color: "#6D6A66" }, grid: { color: "#EEE8DE" } },
    x: {
      ticks: { color: "#6D6A66", maxRotation: 45 },
      grid: { color: "#EEE8DE" },
    },
  },
};

export function SnapshotComparisonChart({ rows }) {
  const data = {
    labels: rows.map((row) => row.label),
    datasets: [
      {
        label: "Aligned patients",
        data: rows.map((row) => row.aligned),
        backgroundColor: COLORS.blue,
        borderRadius: 4,
      },
      {
        label: "Marketable patients",
        data: rows.map((row) => row.marketablePatients),
        backgroundColor: COLORS.green,
        borderRadius: 4,
      },
    ],
  };
  return (
    <div style={{ ...cardStyle }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.88rem",
          marginBottom: "0.15rem",
        }}
      >
        Aligned patients vs marketable patients
      </div>
      <div
        style={{
          fontSize: "0.72rem",
          color: "#7a7467",
          marginBottom: "0.5rem",
        }}
      >
        <span style={{ color: COLORS.blue, fontWeight: 700 }}>■</span> Aligned patients &nbsp;
        <span style={{ color: COLORS.green, fontWeight: 700 }}>■</span> Marketable patients
      </div>
      <div style={{ height: 240 }}>
        <Bar data={data} options={chartDefaults} />
      </div>
    </div>
  );
}

export function ScheduledTrendChart({ rows, xKey, xLabel }) {
  const data = {
    labels: rows.map((row) => row[xKey]),
    datasets: [
      {
        label: "Scheduled",
        data: rows.map((row) => row.scheduled),
        backgroundColor: COLORS.blue,
        borderRadius: 4,
      },
      {
        label: "Enrolled",
        data: rows.map((row) => row.enrolled),
        backgroundColor: COLORS.green,
        borderRadius: 4,
      },
      {
        label: "No-show",
        data: rows.map((row) => row.noShow),
        backgroundColor: COLORS.red,
        borderRadius: 4,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle, flex: "1 1 340px" }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.88rem",
          marginBottom: "0.15rem",
        }}
      >
        Scheduled / Enrolled / No-show — {xLabel}
      </div>
      <div
        style={{
          fontSize: "0.72rem",
          color: "#7a7467",
          marginBottom: "0.5rem",
        }}
      >
        <span style={{ color: COLORS.blue, fontWeight: 700 }}>■</span> Scheduled &nbsp;
        <span style={{ color: COLORS.green, fontWeight: 700 }}>■</span> Enrolled &nbsp;
        <span style={{ color: COLORS.red, fontWeight: 700 }}>■</span> No-show
      </div>
      <div style={{ height: 200 }}>
        <Bar data={data} options={chartDefaults} />
      </div>
    </div>
  );
}

export function RatesTrendChart({ rows, xKey, xLabel }) {
  const data = {
    labels: rows.map((row) => row[xKey]),
    datasets: [
      {
        label: "Conversion rate",
        data: rows.map((row) => row.conversionRate),
        borderColor: COLORS.green,
        backgroundColor: "rgba(42,157,143,0.13)",
        fill: true,
        tension: 0.25,
        pointRadius: 3,
      },
      {
        label: "No-show rate",
        data: rows.map((row) => row.noShowRate),
        borderColor: COLORS.red,
        backgroundColor: "rgba(200,76,53,0.08)",
        fill: true,
        tension: 0.25,
        pointRadius: 3,
      },
      {
        label: "Not-eligible rate",
        data: rows.map((row) => row.notEligibleRate),
        borderColor: COLORS.orange,
        borderDash: [5, 4],
        backgroundColor: "transparent",
        tension: 0.2,
        pointRadius: 2,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle, flex: "1 1 340px" }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.88rem",
          marginBottom: "0.15rem",
        }}
      >
        Rates — {xLabel}
      </div>
      <div
        style={{
          fontSize: "0.72rem",
          color: "#7a7467",
          marginBottom: "0.5rem",
        }}
      >
        <span style={{ color: COLORS.green, fontWeight: 700 }}>■</span> Conversion &nbsp;
        <span style={{ color: COLORS.red, fontWeight: 700 }}>■</span> No-show &nbsp;
        <span style={{ color: COLORS.orange, fontWeight: 700 }}>—</span> Not-eligible
      </div>
      <div style={{ height: 200 }}>
        <Line
          data={data}
          options={{
            ...chartDefaults,
            plugins: { legend: { display: false } },
            scales: {
              y: {
                min: 0,
                max: 1,
                ticks: { callback: (value) => asPct(value), color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
              x: {
                ticks: { color: "#6D6A66", maxRotation: 45 },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function EnrolledSubtypeChart({ rows, xKey, xLabel }) {
  const data = {
    labels: rows.map((row) => row[xKey]),
    datasets: [
      {
        label: "E1 enrolled",
        data: rows.map((row) => row.e1),
        backgroundColor: COLORS.blue,
        stack: "e",
        borderRadius: 2,
      },
      {
        label: "E2 pending referral",
        data: rows.map((row) => row.e2),
        backgroundColor: COLORS.green,
        stack: "e",
        borderRadius: 2,
      },
      {
        label: "E3 add. call pending",
        data: rows.map((row) => row.e3),
        backgroundColor: COLORS.orange,
        stack: "e",
        borderRadius: 2,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle, flex: "1 1 340px" }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.88rem",
          marginBottom: "0.15rem",
        }}
      >
        Enrolled breakdown (E1/E2/E3) — {xLabel}
      </div>
      <div
        style={{
          fontSize: "0.72rem",
          color: "#7a7467",
          marginBottom: "0.5rem",
        }}
      >
        <span style={{ color: COLORS.blue, fontWeight: 700 }}>■</span> E1 &nbsp;
        <span style={{ color: COLORS.green, fontWeight: 700 }}>■</span> E2 &nbsp;
        <span style={{ color: COLORS.orange, fontWeight: 700 }}>■</span> E3
      </div>
      <div style={{ height: 200 }}>
        <Bar
          data={data}
          options={{
            ...chartDefaults,
            scales: {
              x: {
                stacked: true,
                ticks: { color: "#6D6A66", maxRotation: 45 },
                grid: { color: "#EEE8DE" },
              },
              y: {
                stacked: true,
                ticks: { color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function RollupTable({ rows, xKey }) {
  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.8rem",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e0d8" }}>
            {[
              xKey,
              "Sched.",
              "AI",
              "ES",
              "Practice",
              "No-show",
              "Enrolled",
              "E1",
              "E2",
              "E3",
              "Not elig.",
              "Cancelled",
              "No-show %",
              "Conv. %",
            ].map((header) => (
              <th
                key={header}
                style={{
                  padding: "0.35rem 0.5rem",
                  textAlign: header === xKey ? "left" : "right",
                  fontWeight: 600,
                  color: "#4a4540",
                  whiteSpace: "nowrap",
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row[xKey]}
              style={{
                background: index % 2 === 0 ? "#fafaf8" : "#fff",
                borderBottom: "1px solid #f0ede8",
              }}
            >
              <td style={{ padding: "0.3rem 0.5rem", fontWeight: 500 }}>{row[xKey]}</td>
              {[
                row.scheduled,
                row.scheduledAi,
                row.scheduledEs,
                row.scheduledPractice,
                row.noShow,
                row.enrolled,
                row.e1,
                row.e2,
                row.e3,
                row.notEligible,
                row.cancelled,
              ].map((value, valueIndex) => (
                <td
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed column order
                  key={valueIndex}
                  style={{ padding: "0.3rem 0.5rem", textAlign: "right" }}
                >
                  {value ?? "—"}
                </td>
              ))}
              <td
                style={{
                  padding: "0.3rem 0.5rem",
                  textAlign: "right",
                  color: (row.noShowRate || 0) > 0.25 ? COLORS.red : "#4a4540",
                }}
              >
                {asPct(row.noShowRate)}
              </td>
              <td
                style={{
                  padding: "0.3rem 0.5rem",
                  textAlign: "right",
                  color:
                    (row.conversionRate || 0) >= 0.55
                      ? COLORS.green
                      : (row.conversionRate || 0) < 0.4
                        ? COLORS.red
                        : COLORS.orange,
                }}
              >
                {asPct(row.conversionRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EsOpsFailureTable({ rows }) {
  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.82rem",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e0d8" }}>
            {["cal.com link", "ES name"].map((header) => (
              <th
                key={header}
                style={{
                  padding: "0.45rem 0.55rem",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#4a4540",
                  whiteSpace: "nowrap",
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={2} style={{ padding: "0.9rem 0.55rem", color: COLORS.gray }}>
                No unmatched active past bookings for this analysis day.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={row.calUid || `${row.calComLink}-${index}`}
                style={{
                  background: index % 2 === 0 ? "#fafaf8" : "#fff",
                  borderBottom: "1px solid #f0ede8",
                }}
              >
                <td style={{ padding: "0.45rem 0.55rem" }}>
                  {row.calComLink ? (
                    <a
                      href={row.calComLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: COLORS.blue, fontWeight: 600 }}
                    >
                      {row.calUid || row.calComLink}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: "0.45rem 0.55rem" }}>{row.esName || "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function representativeEnrollmentCall(row) {
  const call = [...(row.matchingCalls || [])].sort((left, right) => {
    const reviewPriority = (candidate) =>
      candidate.callCategory === "enrollment"
        ? 2
        : Object.keys(candidate.scores || {}).length > 0
          ? 1
          : 0;
    return (
      reviewPriority(right) - reviewPriority(left) ||
      Number(right.connectedToHuman) - Number(left.connectedToHuman) ||
      Number(right.durationSeconds || 0) - Number(left.durationSeconds || 0)
    );
  })[0];
  return call
    ? {
        ...call,
        carespaceLink: row.carespaceLink,
        esName: row.esName,
      }
    : null;
}

export function deriveRepresentativeCalls(rows, predicate) {
  return rows
    .filter(predicate)
    .map(representativeEnrollmentCall)
    .filter(Boolean)
    .sort((left, right) =>
      String(right.startedAt || "").localeCompare(String(left.startedAt || "")),
    );
}

export function isComplianceConversion(row) {
  return row.isConvertible && (row.isEnrolled || row.isPendingReferral || row.isPendingSecondCall);
}

export function TranscriptLink({ callId }) {
  return callId ? (
    <a
      href={`https://dialpad.com/callhistory/callreview/${encodeURIComponent(callId)}`}
      target="_blank"
      rel="noreferrer"
      style={{ color: COLORS.blue, fontWeight: 600 }}
    >
      Transcript
    </a>
  ) : (
    "—"
  );
}

export function FailuresToConvertTable({ rows }) {
  return <EnrollmentFailureTable rows={rows} />;
}

export function MissingObjectionReasonsTable({ rows }) {
  return <EnrollmentFailureTable rows={rows} missingObjectionReasonsOnly />;
}

function EnrollmentFailureTable({ rows, missingObjectionReasonsOnly = false }) {
  const [sort, setSort] = useState({ key: "esName", direction: "asc" });
  const columns = useMemo(
    () => [
      {
        key: "carespace",
        label: missingObjectionReasonsOnly ? "CS name" : "CS link",
        getValue: (row) => (missingObjectionReasonsOnly ? row.carespaceName : row.carespaceLink),
        render: (row) =>
          row.carespaceLink ? (
            <a
              href={row.carespaceLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: COLORS.blue, fontWeight: 600 }}
            >
              {missingObjectionReasonsOnly
                ? row.carespaceName || "Carespace name unavailable"
                : "Carespace"}
            </a>
          ) : missingObjectionReasonsOnly ? (
            row.carespaceName || "Carespace unavailable"
          ) : (
            "—"
          ),
      },
      {
        key: "esName",
        label: "ES name",
        getValue: (row) => row.esName,
        render: (row) => row.esName || "—",
      },
      ...(missingObjectionReasonsOnly
        ? [
            {
              key: "bookingStartAt",
              label: "Call date (ET)",
              getValue: (row) => row.bookingStartAt,
              render: (row) => formatEtDateTime(row.bookingStartAt),
              whiteSpace: "nowrap",
            },
            {
              key: "documentation",
              label: "Encounter documentation",
              getValue: (row) => row.hasEnrollmentEncounter,
              render: (row) =>
                row.hasEnrollmentEncounter
                  ? "Objection reasons missing"
                  : "No enrollment encounter",
            },
          ]
        : []),
      {
        key: "transcript",
        label: "Dialpad transcript link",
        getValue: (row) => row.id,
        render: (row) => <TranscriptLink callId={row.id} />,
      },
      {
        key: "duration",
        label: "Duration",
        getValue: (row) => row.durationSeconds,
        render: (row) => formatDuration(row.durationSeconds),
        align: "right",
        whiteSpace: "nowrap",
      },
      {
        key: "prompt",
        label: "Prompt",
        getValue: (row) =>
          `${row.promptName || ""}${row.promptVersion ? ` v${row.promptVersion}` : ""}`,
        render: (row) => (
          <>
            {row.promptName || "—"}
            {row.promptVersion ? ` v${row.promptVersion}` : ""}
          </>
        ),
        whiteSpace: "nowrap",
      },
      {
        key: "feedback",
        label: "Feedback",
        getValue: (row) => row.feedback,
        render: (row) => row.feedback || "—",
        minWidth: 280,
      },
    ],
    [missingObjectionReasonsOnly],
  );
  const failureRows = useMemo(() => {
    const column = columns.find((candidate) => candidate.key === sort.key);
    return rows
      .filter(failedToConvert)
      .filter((row) => !missingObjectionReasonsOnly || row.hasEnrollmentObjectionReasons === false)
      .map((row) => ({
        ...representativeEnrollmentCall(row),
        calUid: row.calUid,
        bookingStartAt: row.bookingStartAt,
        carespaceLink: row.carespaceLink,
        esName: row.esName,
        carespaceName: row.carespaceName,
        hasEnrollmentEncounter: row.hasEnrollmentEncounter,
      }))
      .sort((left, right) => {
        const comparison = String(column?.getValue(left) ?? "").localeCompare(
          String(column?.getValue(right) ?? ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        );
        if (comparison !== 0) {
          return sort.direction === "asc" ? comparison : -comparison;
        }
        return String(right.startedAt || "").localeCompare(String(left.startedAt || ""));
      });
  }, [columns, rows, sort, missingObjectionReasonsOnly]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <div style={{ ...subStyle, marginBottom: "0.75rem" }}>
        {failureRows.length} bookings.{" "}
        {missingObjectionReasonsOnly
          ? "Convertible calls that failed to convert with no structured objection (decline) reasons in the enrollment encounter. Includes calls with no enrollment encounter."
          : "Same filters as EOD Dash: Convertible = Yes and Failed to convert = Yes. Call details appear when available."}
      </div>
      <table
        aria-label={
          missingObjectionReasonsOnly
            ? "Missing objection reasons calls"
            : "Failures to convert calls"
        }
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.82rem",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e0d8" }}>
            {columns.map((header) => (
              <th
                key={header.key}
                aria-sort={
                  sort.key === header.key
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
                style={{
                  padding: "0.45rem 0.55rem",
                  textAlign: header.align || "left",
                  fontWeight: 600,
                  color: "#4a4540",
                  whiteSpace: "nowrap",
                  minWidth: header.minWidth,
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(header.key)}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "inherit",
                    cursor: "pointer",
                    font: "inherit",
                    fontWeight: "inherit",
                    padding: 0,
                  }}
                >
                  {header.label}
                  {sort.key === header.key ? (sort.direction === "asc" ? " ↑" : " ↓") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {failureRows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  padding: "1.4rem 0.55rem",
                  color: COLORS.gray,
                  textAlign: "center",
                }}
              >
                {missingObjectionReasonsOnly
                  ? "No missing objection reasons in this date range."
                  : "No failures to convert in this date range."}
              </td>
            </tr>
          ) : (
            failureRows.map((row, index) => (
              <tr
                key={`${row.calUid}-${row.bookingStartAt}`}
                style={{
                  background: index % 2 === 0 ? "#fafaf8" : "#fff",
                  borderBottom: "1px solid #f0ede8",
                }}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      padding: "0.45rem 0.55rem",
                      textAlign: column.align || "left",
                      minWidth: column.minWidth,
                      whiteSpace: column.whiteSpace,
                    }}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const DEFAULT_COMPLIANCE_FILTERS = {
  consentReadScore: "< 0.4",
  consentFailedScore: "= 1",
};

export function matchesComplianceFilter(value, rawFilter, numeric = false) {
  const filter = rawFilter.trim();
  if (!filter) return true;

  if (numeric) {
    const comparison = filter.match(/^(<=|>=|=|<|>)\s*(-?\d+(?:\.\d+)?)$/);
    const number = Number(value);
    if (comparison && Number.isFinite(number)) {
      const target = Number(comparison[2]);
      if (comparison[1] === "<") return number < target;
      if (comparison[1] === "<=") return number <= target;
      if (comparison[1] === ">") return number > target;
      if (comparison[1] === ">=") return number >= target;
      return number === target;
    }
  }

  return String(value ?? "")
    .toLocaleLowerCase()
    .includes(filter.toLocaleLowerCase());
}

export function ComplianceTable({ rows }) {
  const [filters, setFilters] = useState(DEFAULT_COMPLIANCE_FILTERS);
  const [sort, setSort] = useState({ key: "esName", direction: "asc" });
  const complianceRows = useMemo(
    () =>
      deriveRepresentativeCalls(rows, isComplianceConversion).filter(
        (row) =>
          typeof row.durationSeconds === "number" &&
          Number.isFinite(row.durationSeconds) &&
          row.durationSeconds >= 15 * 60,
      ),
    [rows],
  );
  const columns = [
    {
      key: "carespace",
      label: "CS link",
      getValue: (row) => row.carespaceLink,
      render: (row) =>
        row.carespaceLink ? (
          <a
            href={row.carespaceLink}
            target="_blank"
            rel="noreferrer"
            style={{ color: COLORS.blue, fontWeight: 600 }}
          >
            Carespace
          </a>
        ) : (
          "—"
        ),
    },
    {
      key: "esName",
      label: "ES name",
      getValue: (row) => row.esName,
      render: (row) => row.esName || "—",
      whiteSpace: "nowrap",
    },
    {
      key: "transcript",
      label: "Dialpad transcript link",
      getValue: (row) => row.id,
      render: (row) => <TranscriptLink callId={row.id} />,
    },
    {
      key: "duration",
      label: "Duration",
      getValue: (row) => formatDuration(row.durationSeconds),
      sortValue: (row) => row.durationSeconds,
      render: (row) => formatDuration(row.durationSeconds),
      align: "right",
      whiteSpace: "nowrap",
    },
    {
      key: "consentReadScore",
      label: "Consent read score",
      getValue: (row) => row.scores?.consent_read?.value,
      render: (row) => row.scores?.consent_read?.value ?? "—",
      align: "right",
      fontWeight: 700,
      numericFilter: true,
    },
    {
      key: "consentReadRationale",
      label: "Consent read rationale",
      getValue: (row) => row.scores?.consent_read?.rationale,
      render: (row) => row.scores?.consent_read?.rationale || "—",
      minWidth: 260,
    },
    {
      key: "consentFailedScore",
      label: "Consent failed score",
      getValue: (row) => row.scores?.consent_failed?.value,
      render: (row) => row.scores?.consent_failed?.value ?? "—",
      align: "right",
      fontWeight: 700,
      numericFilter: true,
    },
    {
      key: "consentFailedRationale",
      label: "Consent failed rationale",
      getValue: (row) => row.scores?.consent_failed?.rationale,
      render: (row) => row.scores?.consent_failed?.rationale || "—",
      minWidth: 260,
    },
    {
      key: "feedback",
      label: "Feedback",
      getValue: (row) => row.feedback,
      render: (row) => row.feedback || "—",
      minWidth: 280,
    },
    {
      key: "prompt",
      label: "Prompt",
      getValue: (row) =>
        `${row.promptName || ""}${row.promptVersion ? ` v${row.promptVersion}` : ""}`,
      render: (row) => (
        <>
          {row.promptName || "—"}
          {row.promptVersion ? ` v${row.promptVersion}` : ""}
        </>
      ),
      whiteSpace: "nowrap",
    },
  ];
  const scoreColumns = columns.filter((column) => column.numericFilter);
  const nonScoreColumns = columns.filter((column) => !column.numericFilter);
  const visibleRows = complianceRows.filter((row) => {
    const matchesOtherFilters = nonScoreColumns.every((column) =>
      matchesComplianceFilter(column.getValue(row), filters[column.key] || ""),
    );
    const activeScoreColumns = scoreColumns.filter((column) =>
      Boolean(filters[column.key]?.trim()),
    );
    const matchesEitherScore =
      activeScoreColumns.length === 0 ||
      activeScoreColumns.some((column) =>
        matchesComplianceFilter(column.getValue(row), filters[column.key], true),
      );
    return matchesOtherFilters && matchesEitherScore;
  });
  const sortColumn = columns.find((column) => column.key === sort.key);
  visibleRows.sort((left, right) => {
    const getValue = sortColumn?.sortValue || sortColumn?.getValue;
    const comparison = String(getValue?.(left) ?? "").localeCompare(
      String(getValue?.(right) ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" },
    );
    if (comparison !== 0) {
      return sort.direction === "asc" ? comparison : -comparison;
    }
    return String(right.startedAt || "").localeCompare(String(left.startedAt || ""));
  });

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <div style={{ ...subStyle, marginTop: 0, marginBottom: "0.75rem" }}>
        Calls from converted Daily conversion details rows in the selected date range. Score filters
        match either condition. Showing {visibleRows.length} of {complianceRows.length}.
      </div>
      <table
        aria-label="Enrollment compliance calls"
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.82rem",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e0d8" }}>
            {columns.map((column) => (
              <th
                key={column.key}
                aria-sort={
                  sort.key === column.key
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
                style={{
                  padding: "0.45rem 0.55rem",
                  textAlign: column.align || "left",
                  fontWeight: 600,
                  color: "#4a4540",
                  whiteSpace: "nowrap",
                  minWidth: column.minWidth,
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(column.key)}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "inherit",
                    cursor: "pointer",
                    font: "inherit",
                    fontWeight: "inherit",
                    padding: 0,
                  }}
                >
                  {column.label}
                  {sort.key === column.key ? (sort.direction === "asc" ? " ↑" : " ↓") : ""}
                </button>
              </th>
            ))}
          </tr>
          <tr>
            {columns.map((column) => (
              <th key={`${column.key}-filter`} style={{ padding: "0 0.45rem 0.45rem" }}>
                <input
                  aria-label={`Filter Compliance ${column.label}`}
                  type="search"
                  value={filters[column.key] || ""}
                  placeholder="Filter…"
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      [column.key]: event.target.value,
                    }))
                  }
                  style={{ width: "100%", fontSize: "0.72rem" }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  padding: "1.4rem 0.55rem",
                  color: COLORS.gray,
                  textAlign: "center",
                }}
              >
                {Object.keys(filters).some(
                  (key) => filters[key] !== (DEFAULT_COMPLIANCE_FILTERS[key] || ""),
                )
                  ? "No compliance calls match the current column filters."
                  : "No converted calls in this date range."}
              </td>
            </tr>
          ) : (
            visibleRows.map((row, index) => (
              <tr
                key={`${row.id}-${row.startedAt}`}
                style={{
                  background: index % 2 === 0 ? "#fafaf8" : "#fff",
                  borderBottom: "1px solid #f0ede8",
                  verticalAlign: "top",
                }}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      padding: "0.45rem 0.55rem",
                      textAlign: column.align || "left",
                      fontWeight: column.fontWeight,
                      minWidth: column.minWidth,
                      whiteSpace: column.whiteSpace,
                    }}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const FLAG_COLORS = {
  "No outbound call at scheduled time": COLORS.red,
  "Outbound call outside scheduled time window": COLORS.orange,
  "No second outbound attempt": COLORS.orange,
};

export function EsOpsCallFailureTable({ rows }) {
  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.82rem",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e0d8" }}>
            {["cal.com link", "Carespace", "ES name", "Flag(s)"].map((header) => (
              <th
                key={header}
                style={{
                  padding: "0.45rem 0.55rem",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#4a4540",
                  whiteSpace: "nowrap",
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ padding: "0.9rem 0.55rem", color: COLORS.gray }}>
                No active past bookings raised a call flag for this analysis day.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={row.calUid || `${row.calComLink}-${index}`}
                style={{
                  background: index % 2 === 0 ? "#fafaf8" : "#fff",
                  borderBottom: "1px solid #f0ede8",
                }}
              >
                <td style={{ padding: "0.45rem 0.55rem" }}>
                  {row.calComLink ? (
                    <a
                      href={row.calComLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: COLORS.blue, fontWeight: 600 }}
                    >
                      {row.calUid || row.calComLink}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: "0.45rem 0.55rem" }}>
                  {row.carespaceLink ? (
                    <a
                      href={row.carespaceLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: COLORS.blue, fontWeight: 600 }}
                    >
                      Carespace
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: "0.45rem 0.55rem" }}>{row.esName || "—"}</td>
                <td style={{ padding: "0.45rem 0.55rem" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.35rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {(row.flags || []).map((flag) => (
                      <span
                        key={flag}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: 6,
                          padding: "0.18rem 0.45rem",
                          background: `${FLAG_COLORS[flag] || COLORS.gray}1A`,
                          color: FLAG_COLORS[flag] || COLORS.gray,
                          border: `1px solid ${FLAG_COLORS[flag] || COLORS.gray}`,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {flag}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function CallsSummarySection({ calls }) {
  const current = calls?.current || {};
  const previous = calls?.previous || {};
  const currentMetrics = summarizeCallMetrics(current);
  const previousMetrics = summarizeCallMetrics(previous);
  const rateRows = (calls?.last7Days || []).map((row) => ({
    date: row.date,
    ...summarizeCallMetrics(row),
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.15rem" }}>
      <div style={subStyle}>
        Enrollment / referral bookings, reported on the original appointment date in Eastern time.
        Rescheduled means a confirmed replacement. Same-time specialist changes are excluded; the
        final appointment is counted once. Cancellation timing does not determine the bucket.
      </div>
      <div>
        <div
          style={{
            ...labelStyle,
            fontWeight: 600,
            color: "#242124",
            marginBottom: "0.45rem",
          }}
        >
          Daily outcomes and rates
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.75rem",
          }}
        >
          {[
            {
              label: "Failed to convert",
              displayValue: fmtN(currentMetrics.callsNotConverted),
              detail: "Convertible Yes and Enrolled No in daily details",
              accent: COLORS.red,
            },
            {
              label: "No-show rate",
              value: currentMetrics.noShowRate,
              detail: "no-show / remaining bookings",
              accent: COLORS.red,
            },
            {
              label: "Not-eligible rate",
              value: currentMetrics.notEligibleRate,
              detail: "not eligible / (convertible patients + not eligible)",
              accent: COLORS.orange,
            },
            {
              label: "Conversion rate",
              value: currentMetrics.conversionRateConvertiblePatients,
              displayValue: `${(currentMetrics.conversionRateConvertiblePatients * 100).toFixed(1)}%`,
              detail: "Enrolled Yes / Convertible Yes in daily details",
              accent: COLORS.green,
            },
          ].map((metric) => (
            <div key={metric.label} role="group" aria-label={metric.label} style={cardStyle}>
              <div style={labelStyle}>{metric.label}</div>
              <div
                style={{
                  ...valueStyle,
                  color: metric.accent,
                  fontSize: "2.25rem",
                }}
              >
                {metric.displayValue ?? asPct(metric.value)}
              </div>
              <div style={subStyle}>{metric.detail}</div>
            </div>
          ))}
        </div>
      </div>
      <FormulaRow
        caption="Bookings identity"
        items={[
          {
            label: "Total enrollment / referral bookings",
            value: current.totalBookings,
            previousValue: previous.totalBookings,
            accent: COLORS.blue,
          },
          { type: "operator", symbol: "=" },
          {
            label: "Canceled",
            value: current.cancelledCalls,
            previousValue: previous.cancelledCalls,
            accent: COLORS.red,
          },
          { type: "operator", symbol: "+" },
          {
            label: "Rescheduled",
            value: current.rescheduledCalls,
            previousValue: previous.rescheduledCalls,
            accent: COLORS.orange,
          },
          { type: "operator", symbol: "+" },
          {
            label: "Remaining bookings",
            value: current.remainingCalls,
            previousValue: previous.remainingCalls,
            accent: COLORS.green,
          },
        ]}
      />
      <FormulaRow
        caption="Convertible identity"
        items={[
          {
            label: "Convertible patients",
            value: currentMetrics.convertiblePatients,
            previousValue: previousMetrics.convertiblePatients,
            accent: COLORS.orange,
          },
          { type: "operator", symbol: "=" },
          {
            label: "Remaining bookings",
            value: current.remainingCalls,
            previousValue: previous.remainingCalls,
            accent: COLORS.green,
          },
          { type: "operator", symbol: "-" },
          {
            label: "Calls not eligible",
            value: current.notEligibleCalls,
            previousValue: previous.notEligibleCalls,
            accent: COLORS.gray,
          },
          { type: "operator", symbol: "-" },
          {
            label: "Calls marked as no-show",
            value: current.noShowCalls,
            previousValue: previous.noShowCalls,
            accent: COLORS.red,
          },
          ...(currentMetrics.exclusionOverlapAdjustment ||
          previousMetrics.exclusionOverlapAdjustment
            ? [
                { type: "operator", symbol: "+" },
                {
                  label: "Overlapping exclusions",
                  value: currentMetrics.exclusionOverlapAdjustment,
                  previousValue: previousMetrics.exclusionOverlapAdjustment,
                  accent: COLORS.blue,
                },
              ]
            : []),
        ]}
      />
      <FormulaRow
        caption="Converted identity"
        items={[
          {
            label: "Calls converted",
            value: currentMetrics.callsConverted,
            previousValue: previousMetrics.callsConverted,
            accent: COLORS.green,
          },
          { type: "operator", symbol: "=" },
          {
            label: "Submitted",
            value: current.enrolledCalls,
            previousValue: previous.enrolledCalls,
            accent: COLORS.blue,
          },
          { type: "operator", symbol: "+" },
          {
            label: "Pending referral",
            value: current.pendingReferralCalls,
            previousValue: previous.pendingReferralCalls,
            accent: COLORS.green,
          },
          { type: "operator", symbol: "+" },
          {
            label: "Pending 2nd PT/CG call",
            value: current.pendingSecondCallCalls,
            previousValue: previous.pendingSecondCallCalls,
            accent: COLORS.orange,
          },
        ]}
      />
      <EodRatesChart rows={rateRows} />
    </div>
  );
}

export function statusLabel(value) {
  if (!value) return "—";
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

export function BooleanValue({ value }) {
  return (
    <span
      style={{
        display: "inline-flex",
        justifyContent: "center",
        minWidth: 38,
        padding: "0.16rem 0.38rem",
        borderRadius: 999,
        background: value ? "#dcfce7" : "#f3f4f6",
        color: value ? "#166534" : "#6b7280",
        fontWeight: 700,
        fontSize: "0.72rem",
      }}
    >
      {value ? "Yes" : "No"}
    </span>
  );
}

export function failedToConvert(row) {
  return row.isConvertible && !isComplianceConversion(row);
}

export function DailyConversionDetailsTable({
  rows = [] as any[],
  downloadFilename = "daily-conversion-details.xlsx",
  emptyMessage = "No bookings for this day.",
}: {
  downloadFilename?: string;
  emptyMessage?: string;
  rows?: any[];
}) {
  const [sort, setSort] = useState({ key: "bookingStartAt", direction: "asc" });
  const [filters, setFilters] = useState({});

  const columns = useMemo(
    () => [
      {
        key: "calUid",
        label: "Cal.com link",
        getValue: (row) => row.calUid,
        render: (row) =>
          row.calComLink ? (
            <a href={row.calComLink} target="_blank" rel="noreferrer">
              {row.calUid}
            </a>
          ) : (
            "—"
          ),
      },
      {
        key: "bookingStartAt",
        label: "Date + time in ET",
        getValue: (row) => row.bookingStartAt,
        exportValue: (row) => formatEtTimestamp(row.bookingStartAt),
        render: (row) => formatEtTimestamp(row.bookingStartAt),
      },
      { key: "esName", label: "ES name", getValue: (row) => row.esName },
      {
        key: "clinicName",
        label: "Clinic name",
        getValue: (row) => row.clinicName,
      },
      {
        key: "carespaceId",
        label: "Matched carespace",
        getValue: (row) => row.carespaceId,
        render: (row) =>
          row.carespaceLink ? (
            <a href={row.carespaceLink} target="_blank" rel="noreferrer">
              {row.carespaceId}
            </a>
          ) : (
            "—"
          ),
      },
      {
        key: "guideStatus",
        label: "CS status at EOD",
        getValue: (row) => statusLabel(row.guideStatus),
      },
      {
        key: "enrollmentStatus",
        label: "Enrollment status at EOD",
        getValue: (row) => statusLabel(row.enrollmentStatus),
      },
      {
        key: "isCancelled",
        label: "Canceled",
        type: "boolean",
        getValue: (row) => row.isCancelled,
        render: (row) => <BooleanValue value={row.isCancelled} />,
      },
      {
        key: "cancelReason",
        label: "Cancel reason",
        getValue: (row) => row.cancelReason,
      },
      {
        key: "cancelledBy",
        label: "Canceled by whom",
        getValue: (row) => row.cancelledBy,
      },
      {
        key: "matchingCalls",
        label: "Matching Dialpad call",
        getValue: (row) =>
          (row.matchingCalls || [])
            .map(
              (call) =>
                `${call.direction || ""} ${call.startedAt || ""} ${call.durationSeconds || ""} ${call.id || ""}`,
            )
            .join(" "),
        render: (row) =>
          row.matchingCalls?.length ? (
            <div
              title={row.matchingCalls
                .map(
                  (call) =>
                    `${statusLabel(call.direction)} · ${formatEtTimestamp(call.startedAt)} · ${formatDuration(call.durationSeconds)} · Call ${call.id}`,
                )
                .join("\n")}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: "0.3rem",
                width: 280,
                maxHeight: "2.4rem",
                lineHeight: "1.2rem",
                overflow: "hidden",
              }}
            >
              {row.matchingCalls.map((call) => (
                <div key={`${call.id}-${call.startedAt}`}>
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <strong>{statusLabel(call.direction)}</strong>
                    {" · "}
                    {formatEtTimestamp(call.startedAt)}
                    {" · "}
                    {formatDuration(call.durationSeconds)}
                  </div>
                  <div style={{ color: COLORS.gray, fontSize: "0.68rem" }}>Call {call.id}</div>
                </div>
              ))}
            </div>
          ) : (
            "—"
          ),
      },
      {
        key: "connectedToHuman",
        label: "Connected to a human",
        type: "boolean",
        getValue: (row) => row.connectedToHuman,
        render: (row) => <BooleanValue value={row.connectedToHuman} />,
      },
      {
        key: "isNoShow",
        label: "No-show",
        type: "boolean",
        getValue: (row) => row.isNoShow,
        render: (row) => <BooleanValue value={row.isNoShow} />,
      },
      {
        key: "isRescheduled",
        label: "Rescheduled",
        type: "boolean",
        getValue: (row) => row.isRescheduled,
        render: (row) => <BooleanValue value={row.isRescheduled} />,
      },
      {
        key: "rescheduledCalUid",
        label: "Rescheduled cal link",
        getValue: (row) => row.rescheduledCalUid,
        render: (row) =>
          row.rescheduledCalLink ? (
            <a href={row.rescheduledCalLink} target="_blank" rel="noreferrer">
              {row.rescheduledCalUid}
            </a>
          ) : (
            "—"
          ),
      },
      {
        key: "isNotEligible",
        label: "Not eligible",
        type: "boolean",
        getValue: (row) => row.isNotEligible,
        render: (row) => <BooleanValue value={row.isNotEligible} />,
      },
      {
        key: "notEligibleReason",
        label: "Not eligible reason",
        getValue: (row) => row.notEligibleReason,
      },
      {
        key: "isConvertible",
        label: "Convertible",
        type: "boolean",
        getValue: (row) => row.isConvertible,
        render: (row) => <BooleanValue value={row.isConvertible} />,
      },
      {
        key: "failedToConvert",
        label: "Failed to convert",
        type: "boolean",
        getValue: failedToConvert,
        render: (row) => <BooleanValue value={failedToConvert(row)} />,
      },
      {
        key: "isEnrolled",
        label: "Enrolled",
        type: "boolean",
        getValue: isComplianceConversion,
        render: (row) => <BooleanValue value={isComplianceConversion(row)} />,
      },
      {
        key: "isPendingReferral",
        label: "Pending referral",
        type: "boolean",
        getValue: (row) => row.isPendingReferral,
        render: (row) => <BooleanValue value={row.isPendingReferral} />,
      },
      {
        key: "isPendingSecondCall",
        label: "Pending 2nd call with PT/CG",
        type: "boolean",
        getValue: (row) => row.isPendingSecondCall,
        render: (row) => <BooleanValue value={row.isPendingSecondCall} />,
      },
    ],
    [],
  );

  const visibleRows = useMemo(() => {
    const filtered = rows.filter((row) =>
      columns.every((column) => {
        const filter = filters[column.key];
        if (!filter) return true;
        const value = column.getValue(row);
        if (column.type === "boolean") {
          return Boolean(value) === (filter === "yes");
        }
        return String(value || "")
          .toLocaleLowerCase()
          .includes(filter.toLocaleLowerCase());
      }),
    );
    const column = columns.find((item) => item.key === sort.key);
    if (!column) return filtered;

    return [...filtered].sort((left, right) => {
      const leftValue = column.getValue(left);
      const rightValue = column.getValue(right);
      const comparison = String(leftValue ?? "").localeCompare(
        String(rightValue ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" },
      );
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [columns, filters, rows, sort]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const downloadExcel = () => {
    const header = columns.map((column) => column.label);
    const lines = visibleRows.map((row) =>
      columns
        .map((column) => {
          const value = column.exportValue ? column.exportValue(row) : column.getValue(row);
          const cell = column.type === "boolean" ? (value ? "Yes" : "No") : value || "";
          return `"${String(cell).replaceAll('"', '""')}"`;
        })
        .join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = String(downloadFilename || "enrollment-conversion-details.csv").replace(
      /\.xlsx$/i,
      ".csv",
    );
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
      <div
        style={{
          padding: "0.8rem 0.9rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 700 }}>Daily conversion details</div>
          <div style={subStyle}>
            Connected means a same-day inbound or outbound Dialpad call connected for at least 30
            seconds. Enrolled means a convertible booking with submitted enrollment, pending
            referral, or a pending second PT/CG call. Failed to convert means Convertible Yes and
            Enrolled No for the appointment day. A later scheduling event alone does not count as
            enrollment.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
          <div style={subStyle}>
            Showing {visibleRows.length} of {rows.length}
          </div>
          <button
            type="button"
            onClick={downloadExcel}
            style={{
              border: "1px solid #bfdbfe",
              background: "#fff",
              borderRadius: 6,
              padding: "0.35rem 0.7rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Download CSV
          </button>
        </div>
      </div>
      <div style={{ overflowX: "auto", maxHeight: "70vh" }}>
        <table
          aria-label="Daily conversion details"
          style={{
            width: "max-content",
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: "0.76rem",
          }}
        >
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  aria-sort={
                    sort.key === column.key
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                  style={{
                    padding: "0.5rem 0.55rem 0.35rem",
                    textAlign: "left",
                    background: "#f8fafc",
                    borderTop: "1px solid #e5e7eb",
                    whiteSpace: "nowrap",
                    minWidth: column.type === "boolean" ? 125 : 165,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    style={{
                      border: 0,
                      background: "transparent",
                      padding: 0,
                      font: "inherit",
                      fontWeight: 700,
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    {column.label}
                    {sort.key === column.key ? (sort.direction === "asc" ? " ↑" : " ↓") : ""}
                  </button>
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((column) => (
                <th
                  key={`${column.key}-filter`}
                  style={{
                    padding: "0 0.45rem 0.45rem",
                    background: "#f8fafc",
                    borderBottom: "1px solid #d1d5db",
                  }}
                >
                  {column.type === "boolean" ? (
                    <select
                      aria-label={`Filter ${column.label}`}
                      value={filters[column.key] || ""}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          [column.key]: event.target.value,
                        }))
                      }
                      style={{ width: "100%", fontSize: "0.72rem" }}
                    >
                      <option value="">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <input
                      aria-label={`Filter ${column.label}`}
                      type="search"
                      value={filters[column.key] || ""}
                      placeholder="Filter…"
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          [column.key]: event.target.value,
                        }))
                      }
                      style={{ width: "100%", fontSize: "0.72rem" }}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length ? (
              visibleRows.map((row, rowIndex) => (
                <tr key={row.calUid}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      style={{
                        padding: "0.5rem 0.55rem",
                        borderBottom: "1px solid #f0ede8",
                        verticalAlign: "top",
                        maxWidth: 320,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        background: rowIndex % 2 === 0 ? "#fff" : "#fafaf8",
                      }}
                    >
                      {column.render ? column.render(row) : column.getValue(row) || "—"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ padding: "1rem", color: COLORS.gray }}>
                  {Object.values(filters).some(Boolean)
                    ? "No bookings match the current column filters."
                    : emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EodRatesChart({ rows }) {
  if (!rows.length) return null;

  const data = {
    labels: rows.map((row) => shortWeekdayDateLabel(row.date)),
    datasets: [
      {
        label: "No-show rate",
        data: rows.map((row) => row.noShowRate),
        borderColor: COLORS.red,
        backgroundColor: "rgba(200,76,53,0.08)",
        fill: true,
        tension: 0.25,
        pointRadius: 3,
      },
      {
        label: "Not eligible rate",
        data: rows.map((row) => row.notEligibleRate),
        borderColor: COLORS.orange,
        backgroundColor: "rgba(247,148,29,0.08)",
        fill: true,
        tension: 0.25,
        pointRadius: 3,
      },
      {
        label: "Conversion rate (remaining bookings)",
        data: rows.map(
          (row) => row.conversionRateRemainingBookings ?? row.conversionRateNonCancelledCalls,
        ),
        borderColor: COLORS.green,
        backgroundColor: "rgba(42,157,143,0.1)",
        fill: true,
        tension: 0.25,
        pointRadius: 3,
      },
      {
        label: "Conversion rate (convertible patients)",
        data: rows.map((row) => row.conversionRateConvertiblePatients),
        borderColor: COLORS.blue,
        borderDash: [6, 4],
        backgroundColor: "transparent",
        tension: 0.2,
        pointRadius: 2,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.88rem",
          marginBottom: "0.15rem",
        }}
      >
        Rates - last 7 days
      </div>
      <div
        style={{
          fontSize: "0.72rem",
          color: "#7a7467",
          marginBottom: "0.5rem",
        }}
      >
        <span style={{ color: COLORS.red, fontWeight: 700 }}>■</span> No-show &nbsp;
        <span style={{ color: COLORS.orange, fontWeight: 700 }}>■</span> Not eligible &nbsp;
        <span style={{ color: COLORS.green, fontWeight: 700 }}>■</span> Conversion (remaining
        bookings) &nbsp;
        <span style={{ color: COLORS.blue, fontWeight: 700 }}>—</span> Conversion (convertible
        patients)
      </div>
      <div style={subStyle}>
        No-show = no-show / remaining bookings. Not eligible = not eligible / (convertible patients
        + not eligible).
      </div>
      <div style={{ height: 240, marginTop: "0.5rem" }}>
        <Line
          data={data}
          options={{
            ...chartDefaults,
            plugins: { legend: { display: false } },
            scales: {
              y: {
                min: 0,
                max: 1,
                ticks: { callback: (value) => asPct(value), color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
              x: {
                ticks: { color: "#6D6A66", maxRotation: 45 },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function CumulativeEnrollmentChart({
  title,
  subtitle,
  rows,
  action = null,
}: {
  action?: ReactNode;
  rows: any[];
  subtitle?: string;
  title: string;
}) {
  const highlightIndices = rows
    .map((row, index) => (isWeekendOrHoliday(row.date) ? index : null))
    .filter((value) => value !== null);

  const data = {
    labels: rows.map((row) => shortWeekdayDateLabel(row.date)),
    datasets: [
      {
        label: "Aligned",
        data: rows.map((row) => row.alignedCount),
        backgroundColor: COLORS.blue,
        stack: "states",
        borderRadius: 2,
      },
      {
        label: "Pending with CMS",
        data: rows.map((row) => row.pendingWithCmsCount),
        backgroundColor: COLORS.lightGray,
        stack: "states",
        borderRadius: 2,
      },
      {
        label: "Pending referral",
        data: rows.map((row) => row.pendingReferralCount),
        backgroundColor: COLORS.green,
        stack: "states",
        borderRadius: 2,
      },
      {
        label: "Pending 2nd PT/CG call",
        data: rows.map((row) => row.pendingSecondCallCount),
        backgroundColor: COLORS.orange,
        stack: "states",
        borderRadius: 2,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
          marginBottom: "0.25rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{title}</div>
          {subtitle ? <div style={subStyle}>{subtitle}</div> : null}
        </div>
        {action}
      </div>
      <div style={{ height: 320 }}>
        <Bar
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: { boxWidth: 12, color: "#4a4540" },
              },
              backgroundBands: { indices: highlightIndices },
              valueLabels: { showSegmentValues: true, showTotals: true },
            },
            scales: {
              x: {
                stacked: true,
                ticks: { color: "#6D6A66", maxRotation: 45 },
                grid: { color: "#EEE8DE" },
              },
              y: {
                stacked: true,
                ticks: { color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function ScheduledCallsChart({ rows }) {
  const data = {
    labels: rows.map((row) => shortWeekdayDateLabel(row.date)),
    datasets: [
      {
        label: "AI scheduled",
        data: rows.map((row) => row.aiScheduled),
        backgroundColor: COLORS.blue,
        stack: "scheduled",
        borderRadius: 2,
      },
      {
        label: "ES scheduled",
        data: rows.map((row) => row.esScheduled),
        backgroundColor: COLORS.green,
        stack: "scheduled",
        borderRadius: 2,
      },
      {
        label: "Web-scheduled",
        data: rows.map((row) => row.webScheduled),
        backgroundColor: COLORS.orange,
        stack: "scheduled",
        borderRadius: 2,
      },
      {
        label: "Unclassified",
        data: rows.map((row) => row.unclassifiedScheduled),
        backgroundColor: COLORS.gray,
        stack: "scheduled",
        borderRadius: 2,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.15rem" }}>
        Scheduled enrollment calls
      </div>
      <div style={subStyle}>
        Active Enrollment / Referral bookings, shown in ET for the last 7 days and next 3 days.
      </div>
      <div style={{ height: 300, marginTop: "0.5rem" }}>
        <Bar
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: { boxWidth: 12, color: "#4a4540" },
              },
              valueLabels: { showSegmentValues: true, showTotals: true },
            },
            scales: {
              x: {
                stacked: true,
                ticks: { color: "#6D6A66", maxRotation: 45 },
                grid: { color: "#EEE8DE" },
              },
              y: {
                stacked: true,
                ticks: { color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

const ES_CONVERSION_AXIS_TICKS = [0, 0.25, 0.5, 0.75, 1];

export function EsConversionBarRow({ row, quartileMarkers }) {
  const rate = row.conversionRate ?? 0;
  const lowN = row.eligibleStartedCalls < 10;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "145px minmax(0, 1fr) 38px",
        alignItems: "center",
        gap: "0.45rem",
        minHeight: 27,
      }}
    >
      <div
        style={{
          fontSize: "0.68rem",
          color: "#4a4540",
          textAlign: "right",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={`${row.esName} (n=${row.eligibleStartedCalls})`}
      >
        {row.esName} (n={row.eligibleStartedCalls})
      </div>
      <div
        style={{
          position: "relative",
          height: 16,
          backgroundColor: "#f4f1ea",
          backgroundImage: "linear-gradient(to right, #e5e0d8 1px, transparent 1px)",
          backgroundSize: "25% 100%",
        }}
      >
        <div
          role="img"
          aria-label={`${row.esName} conversion ${asPct(rate)} from ${row.enrolled} enrolled of ${row.eligibleStartedCalls} eligible started calls${lowN ? ", low n" : ""}`}
          data-low-n={lowN ? "true" : "false"}
          style={{
            position: "absolute",
            inset: "0 auto 0 0",
            width: `${rate * 100}%`,
            borderRadius: 3,
            backgroundColor: lowN ? COLORS.lowNGray : COLORS.blue,
          }}
        />
        {quartileMarkers.map((marker) => (
          <div
            key={`${marker.value}-${marker.color}`}
            style={{
              position: "absolute",
              zIndex: 2,
              top: -3,
              bottom: -3,
              left: `${marker.value * 100}%`,
              borderLeft: `2px ${marker.dashed ? "dashed" : "solid"} ${marker.color}`,
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: "0.68rem", fontWeight: 700 }}>{asPct(rate)}</div>
    </div>
  );
}

export function EsConversionAxis() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "145px minmax(0, 1fr) 38px",
        gap: "0.45rem",
        marginTop: "0.2rem",
      }}
    >
      <div />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {ES_CONVERSION_AXIS_TICKS.map((tick) => (
          <span key={tick} style={{ fontSize: "0.62rem", color: COLORS.gray }}>
            {asPct(tick)}
          </span>
        ))}
      </div>
      <div />
    </div>
  );
}

export function EsConversionWeekChart({ week, analysisDate }) {
  const quartiles = esConversionQuartiles(week.rows);
  const eligibleStartedCalls = week.rows.reduce(
    (total, row) => total + row.eligibleStartedCalls,
    0,
  );
  const enrolled = week.rows.reduce((total, row) => total + row.enrolled, 0);
  const overallRate = eligibleStartedCalls ? enrolled / eligibleStartedCalls : 0;
  const isPartialWeek = analysisDate >= week.weekStart && analysisDate < week.weekEnd;
  const periodNote = isPartialWeek ? `through ${shortDateLabel(analysisDate)}` : "complete week";
  const quartileMarkers = quartiles.map((value, index) => ({
    value,
    color: [COLORS.orange, COLORS.purple, COLORS.green][index],
    dashed: index !== 1,
  }));

  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
        {weekRangeLabel(week.weekStart, week.weekEnd)}
      </div>
      <div style={subStyle}>
        {enrolled}/{eligibleStartedCalls} = {asPct(overallRate)} overall · {periodNote}
      </div>
      <div style={{ ...subStyle, marginTop: "0.35rem" }}>
        Quartiles: Q1 {asPct(quartiles[0])}, median {asPct(quartiles[1])}, Q3 {asPct(quartiles[2])}
      </div>
      <fieldset
        aria-label={`ES conversion for ${weekRangeLabel(week.weekStart, week.weekEnd)}`}
        style={{ border: 0, padding: 0, margin: "0.8rem 0 0" }}
      >
        {week.rows.map((row) => (
          <EsConversionBarRow key={row.esName} row={row} quartileMarkers={quartileMarkers} />
        ))}
        <EsConversionAxis />
      </fieldset>
    </div>
  );
}

export function EsConversionSummaryBar({ esName, week, weekLabel }) {
  const lowN = week.eligibleStartedCalls > 0 && week.eligibleStartedCalls < 10;
  const rate = week.conversionRate;

  if (rate == null) {
    return (
      <div
        role="img"
        aria-label={`${esName}, ${weekLabel}: no eligible started calls`}
        style={{ color: COLORS.gray, textAlign: "center" }}
      >
        —
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={`${esName}, ${weekLabel}: ${asPct(rate)} conversion from ${week.enrolled} enrolled of ${week.eligibleStartedCalls} eligible started calls${lowN ? ", low n" : ""}`}
      style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 62,
          height: 13,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "#f4f1ea",
          backgroundImage: "linear-gradient(to right, #e5e0d8 1px, transparent 1px)",
          backgroundSize: "25% 100%",
        }}
      >
        <div
          style={{
            width: `${rate * 100}%`,
            height: "100%",
            borderRadius: 3,
            backgroundColor: lowN ? COLORS.lowNGray : COLORS.blue,
          }}
        />
      </div>
      <span style={{ minWidth: 30, fontSize: "0.68rem", fontWeight: 700 }}>{asPct(rate)}</span>
    </div>
  );
}

export function EsConversionSummaryChart({ rows }) {
  const { weeks, summaries } = summarizeEsConversion(rows);

  return (
    <div style={{ ...cardStyle, marginTop: "1rem" }}>
      <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>4-week ES conversion comparison</div>
      <div style={subStyle}>
        Sorted by overall conversion across all four weeks. Median, min, and max use weekly
        conversion rates with eligible started calls.
      </div>
      <div style={{ overflowX: "auto", marginTop: "0.9rem" }}>
        <table
          aria-label="Four-week ES conversion comparison"
          style={{
            width: "100%",
            minWidth: 980,
            borderCollapse: "collapse",
            fontSize: "0.72rem",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #DDD5C9" }}>
              <th style={{ padding: "0.45rem", textAlign: "left" }}>ES</th>
              <th style={{ padding: "0.45rem", textAlign: "right" }}>Overall</th>
              {weeks.map((week) => (
                <th key={week.weekStart} style={{ minWidth: 135, padding: "0.45rem" }}>
                  {weekRangeLabel(week.weekStart, week.weekEnd)}
                </th>
              ))}
              <th style={{ padding: "0.45rem", textAlign: "right" }}>Median</th>
              <th style={{ padding: "0.45rem", textAlign: "right" }}>Min</th>
              <th style={{ padding: "0.45rem", textAlign: "right" }}>Max</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map((summary) => (
              <tr key={summary.esName} style={{ borderBottom: "1px solid #EEE8DE" }}>
                <th
                  scope="row"
                  style={{
                    maxWidth: 160,
                    padding: "0.55rem 0.45rem",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  {summary.esName}
                </th>
                <td
                  aria-label={`${summary.esName} overall conversion`}
                  style={{ padding: "0.55rem 0.45rem", textAlign: "right" }}
                >
                  <div style={{ fontWeight: 700 }}>
                    {summary.conversionRate == null ? "—" : asPct(summary.conversionRate)}
                  </div>
                  <div style={{ color: COLORS.gray, fontSize: "0.64rem" }}>
                    {summary.enrolled}/{summary.eligibleStartedCalls}
                  </div>
                </td>
                {summary.weeklyRates.map((week, index) => (
                  <td key={weeks[index].weekStart} style={{ padding: "0.55rem 0.45rem" }}>
                    <EsConversionSummaryBar
                      esName={summary.esName}
                      week={week}
                      weekLabel={weekRangeLabel(weeks[index].weekStart, weeks[index].weekEnd)}
                    />
                  </td>
                ))}
                <td style={{ padding: "0.55rem 0.45rem", textAlign: "right" }}>
                  {summary.medianConversion == null ? "—" : asPct(summary.medianConversion)}
                </td>
                <td style={{ padding: "0.55rem 0.45rem", textAlign: "right" }}>
                  {summary.minConversion == null ? "—" : asPct(summary.minConversion)}
                </td>
                <td style={{ padding: "0.55rem 0.45rem", textAlign: "right" }}>
                  {summary.maxConversion == null ? "—" : asPct(summary.maxConversion)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EsConversionCharts({ rows, analysisDate }) {
  const weeks = groupEsConversionWeeks(rows);

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>ES conversion definition</div>
        <div style={{ ...subStyle, lineHeight: 1.55 }}>
          Enrolled ÷ eligible bookings with a uniquely assigned inbound or outbound Dialpad call
          longer than 2 minutes. Weeks run Monday–Sunday in Eastern Time. Gray bars have fewer than
          10 eligible started calls. Vertical lines show Q1 (orange), median (purple), and Q3
          (green). Enrolled includes GUIDE status aligned, submitted, pending, or review, plus New
          with awaiting referral information or an additional-call status.
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 520px), 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        {weeks.map((week) => (
          <EsConversionWeekChart key={week.weekStart} week={week} analysisDate={analysisDate} />
        ))}
      </div>
      <EsConversionSummaryChart rows={rows} />
    </div>
  );
}

export function ClinicMultiSelect({ clinics, selectedClinics, onChange }) {
  // null means "no filter yet" and aggregates as all clinics, so render it
  // as all-checked instead of flashing unchecked before the default applies.
  const selected = new Set(selectedClinics == null ? clinics : selectedClinics);

  return (
    <div style={{ ...cardStyle, padding: "0.9rem 1rem" }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.86rem",
          marginBottom: "0.35rem",
        }}
      >
        Clinics
      </div>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => onChange(clinics)}
          style={{
            border: "1px solid #bfdbfe",
            background: "#fff",
            borderRadius: 6,
            padding: "0.3rem 0.6rem",
            cursor: "pointer",
          }}
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => onChange([])}
          style={{
            border: "1px solid #e5e0d8",
            background: "#fff",
            borderRadius: 6,
            padding: "0.3rem 0.6rem",
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "0.45rem 1rem",
          maxHeight: 260,
          overflowY: "auto",
        }}
      >
        {clinics.map((clinic) => (
          <label
            key={clinic}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.45rem",
              fontSize: "0.82rem",
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(clinic)}
              onChange={(event) => {
                if (event.target.checked) {
                  onChange([...selected, clinic].sort((a, b) => a.localeCompare(b)));
                } else {
                  onChange([...selected].filter((value) => value !== clinic));
                }
              }}
            />
            <span>{clinic}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function NeuroClinicMultiSelect({
  clinics,
  selectedClinicIds,
  onChange,
  label = "Clinics",
  emptyMessage = "No clinics have scheduled neurology visits in this window.",
}) {
  // null means "no filter yet" and aggregates as all clinics, so render it
  // as all-checked instead of flashing unchecked before the default applies.
  const selected = new Set(
    selectedClinicIds == null ? clinics.map((clinic) => clinic.id) : selectedClinicIds,
  );

  return (
    <div style={{ ...cardStyle, padding: "0.9rem 1rem" }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.86rem",
          marginBottom: "0.35rem",
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => onChange(clinics.map((clinic) => clinic.id))}
          style={{
            border: "1px solid #bfdbfe",
            background: "#fff",
            borderRadius: 6,
            padding: "0.3rem 0.6rem",
            cursor: "pointer",
          }}
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => onChange([])}
          style={{
            border: "1px solid #e5e0d8",
            background: "#fff",
            borderRadius: 6,
            padding: "0.3rem 0.6rem",
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      </div>
      {clinics.length ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.45rem 1rem",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {clinics.map((clinic) => (
            <label
              key={clinic.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.45rem",
                fontSize: "0.82rem",
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(clinic.id)}
                onChange={(event) => {
                  if (event.target.checked) {
                    onChange([...selected, clinic.id]);
                  } else {
                    onChange([...selected].filter((value) => value !== clinic.id));
                  }
                }}
              />
              <span>{clinic.name}</span>
            </label>
          ))}
        </div>
      ) : (
        <div style={{ color: COLORS.gray, fontSize: "0.82rem" }}>{emptyMessage}</div>
      )}
    </div>
  );
}

export function BookingSourcesChart({ title, rows }) {
  const data = {
    labels: rows.map((row) => weekRangeLabel(row.weekStart, row.weekEnd)),
    datasets: BOOKING_SOURCE_SERIES.map((series) => ({
      label: series.label,
      data: rows.map((row) => row[series.key]),
      backgroundColor: series.color,
      borderRadius: 4,
    })),
  };

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.15rem" }}>{title}</div>
      <div style={{ ...subStyle, marginTop: 0 }}>
        Cal.com enrollment-call bookings, including legacy titles with “Enrollment” or “Referral”,
        grouped by appointment week in Eastern Time.
      </div>
      <div style={{ height: 300, marginTop: "0.5rem" }}>
        <Bar
          data={data}
          options={{
            ...chartDefaults,
            plugins: {
              legend: { display: true },
              valueLabels: { showTotals: true, showSegmentValues: true },
            },
            scales: {
              y: {
                ...chartDefaults.scales.y,
                stacked: true,
                beginAtZero: true,
                ticks: { color: "#6D6A66", precision: 0 },
              },
              x: {
                ...chartDefaults.scales.x,
                stacked: true,
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function NeuroAppointmentsChart({ rows, anchorDate }) {
  const data = {
    labels: rows.map((row) => shortWeekdayDateLabel(row.date)),
    datasets: [
      {
        label: "Ended outbound Retell call (±14 days)",
        data: rows.map((row) => row.withRetellCallCount),
        backgroundColor: COLORS.blue,
        borderColor: rows.map((row) => (row.date === anchorDate ? COLORS.orange : COLORS.blue)),
        borderWidth: rows.map((row) => (row.date === anchorDate ? 2 : 0)),
        borderRadius: 4,
      },
      {
        label: "No ended outbound Retell call (±14 days)",
        data: rows.map((row) => row.withoutRetellCallCount),
        backgroundColor: COLORS.lightGray,
        borderColor: rows.map((row) =>
          row.date === anchorDate ? COLORS.orange : COLORS.lightGray,
        ),
        borderWidth: rows.map((row) => (row.date === anchorDate ? 2 : 0)),
        borderRadius: 4,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.15rem" }}>
        New-status carespaces with scheduled neurology visits by Retell call status
      </div>
      <div style={{ ...subStyle, marginTop: 0 }}>
        Bars count distinct carespaces per appointment date, split by whether there is an ended
        outbound Retell call in the inclusive window from 14 calendar days before through 14
        calendar days after that date.
      </div>
      <section
        aria-label="Neurology appointment metric definition"
        style={{
          marginTop: "0.65rem",
          padding: "0.65rem 0.75rem",
          borderRadius: 6,
          background: "#f8f6f2",
          color: "#625d54",
          fontSize: "0.76rem",
          lineHeight: 1.45,
        }}
      >
        <div>
          <strong>Cohort:</strong> scheduled neurologist visits for carespaces whose current GUIDE
          status = New. The cohort excludes only enrollment status ineligible_for_guide and
          software-only organizations.
        </div>
        <div style={{ marginTop: "0.3rem" }}>
          <strong>Grain:</strong> each daily bar counts distinct (carespace, appointment date)
          units. The weekly KPI sums those same daily units across Monday–Sunday using the same
          clinic selection.
        </div>
        <div style={{ marginTop: "0.3rem" }}>
          <strong>Call definition:</strong> any ended outbound Retell call in the stated window;
          this does not assert contact or success.
        </div>
        <div style={{ marginTop: "0.3rem" }}>
          <strong>Live metric:</strong> current visit, GUIDE, and enrollment statuses are evaluated
          when the dashboard loads. Later status changes or ended outbound Retell calls—including
          calls after an appointment—can update past bars retroactively.
        </div>
        <div style={{ marginTop: "0.3rem" }}>
          <strong>Date anchor:</strong> Anchored to today ET ({anchorDate}), independent of the
          selected EOD analysis date.
        </div>
      </section>
      <div style={{ height: 300, marginTop: "0.5rem" }}>
        <Bar
          data={data}
          options={{
            ...chartDefaults,
            plugins: {
              legend: { display: true },
              valueLabels: { showTotals: true },
            },
            scales: {
              ...chartDefaults.scales,
              y: {
                ...chartDefaults.scales.y,
                stacked: true,
                beginAtZero: true,
                ticks: { color: "#6D6A66", precision: 0 },
              },
              x: {
                ...chartDefaults.scales.x,
                stacked: true,
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function NeuroAppointmentsKpi({ summary }) {
  const absoluteChange = `${summary.delta > 0 ? "+" : ""}${fmtN(summary.delta)}`;
  const percentChange = formatPctChange(summary.current, summary.prior);
  const comparison = `${absoluteChange} (${percentChange}) vs prior week`;

  return (
    <section
      aria-label={`This week's distinct carespace appointment-days for current GUIDE status New: ${summary.current}, ${comparison}`}
      style={{ ...cardStyle, padding: "1rem 1.2rem" }}
    >
      <div style={labelStyle}>
        This week's distinct carespace appointment-days — current GUIDE status New
      </div>
      <div style={{ ...valueStyle, fontSize: "2.25rem" }}>{fmtN(summary.current)}</div>
      <div
        style={{
          ...subStyle,
          color: summary.delta > 0 ? COLORS.green : summary.delta < 0 ? COLORS.red : COLORS.gray,
          fontWeight: 600,
        }}
      >
        {comparison}
      </div>
    </section>
  );
}

export function MonthlyStackedChart({
  title,
  subtitle,
  rows,
  primaryLabel,
  secondaryLabel,
  primaryColor,
  secondaryColor,
}) {
  if (!rows.length) {
    return (
      <div style={{ ...cardStyle, color: COLORS.gray }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.9rem",
            marginBottom: "0.15rem",
          }}
        >
          {title}
        </div>
        <div style={subStyle}>{subtitle}</div>
        <div style={{ padding: "1.25rem 0" }}>
          No data available for the current clinic selection.
        </div>
      </div>
    );
  }

  const data = {
    labels: rows.map((row) => monthLabel(row.month)),
    datasets: [
      {
        label: primaryLabel,
        data: rows.map((row) => row.primary),
        backgroundColor: primaryColor,
        stack: "monthly",
        borderRadius: 2,
      },
      {
        label: secondaryLabel,
        data: rows.map((row) => row.secondary),
        backgroundColor: secondaryColor,
        stack: "monthly",
        borderRadius: 2,
      },
    ],
  };

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.15rem" }}>{title}</div>
      <div style={subStyle}>{subtitle}</div>
      <div style={{ height: 320, marginTop: "0.5rem" }}>
        <Bar
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: { boxWidth: 12, color: "#4a4540" },
              },
              valueLabels: { showSegmentValues: true, showTotals: true },
            },
            scales: {
              x: {
                stacked: true,
                ticks: { color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
              y: {
                stacked: true,
                ticks: { color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function AlignmentLagNormalizedChart({ rows }) {
  const description =
    "100% stacked mix of CareSpaces first aligned in each month, grouped by days from their referral date to first alignment.";
  if (!rows.some((row) => row.total > 0)) {
    return (
      <div style={{ ...cardStyle, color: COLORS.gray }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.9rem",
            marginBottom: "0.15rem",
          }}
        >
          First aligned by referral-to-alignment lag
        </div>
        <div style={subStyle}>{description}</div>
        <div style={{ padding: "1.25rem 0" }}>
          No data available for the current clinic selection.
        </div>
      </div>
    );
  }

  const data = {
    labels: rows.map((row) => monthLabel(row.month)),
    datasets: ALIGNMENT_LAG_BUCKETS.map((bucket) => ({
      label: bucket.label,
      data: rows.map((row) => (row.total > 0 ? Number(row[bucket.key] || 0) / row.total : 0)),
      counts: rows.map((row) => Number(row[bucket.key] || 0)),
      totals: rows.map((row) => row.total),
      backgroundColor: bucket.color,
      stack: "alignment-lag",
      borderRadius: 2,
    })),
  };

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.15rem" }}>
        First aligned by referral-to-alignment lag
      </div>
      <div style={subStyle}>{description}</div>
      <div style={{ height: 340, marginTop: "0.5rem" }}>
        <Bar
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: { boxWidth: 12, color: "#4a4540" },
              },
              tooltip: {
                callbacks: {
                  label(context) {
                    const count = context.dataset.counts?.[context.dataIndex] || 0;
                    const total = context.dataset.totals?.[context.dataIndex] || 0;
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return `${context.dataset.label}: ${pct}% (${count}/${total})`;
                  },
                },
              },
            },
            scales: {
              x: {
                stacked: true,
                ticks: { color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
              y: {
                stacked: true,
                min: 0,
                max: 1,
                ticks: { callback: (value) => asPct(value), color: "#6D6A66" },
                grid: { color: "#EEE8DE" },
              },
            },
          }}
        />
      </div>
    </div>
  );
}
